import React, { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { api } from "../lib/api";
import { PageHeader, StatusBadge } from "../components/common";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { toast } from "sonner";
import {
  Trash2, Save, Scale, Handshake, ArrowLeft, Plus, ExternalLink,
} from "lucide-react";

const CATEGORIES = [
  "vendor", "employment", "customer", "intellectual-property",
  "corporate", "property", "consumer", "compliance",
  "dispute", "regulatory", "personal", "other",
];
const STATUSES = ["open", "on-hold", "resolved", "archived"];

function statusTone(s) {
  if (s === "resolved") return "success";
  if (s === "on-hold") return "warn";
  if (s === "archived") return "neutral";
  return "info";
}

export default function MatterDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [matter, setMatter] = useState(null);
  const [cases, setCases] = useState([]);
  const [agreements, setAgreements] = useState([]);
  const [saving, setSaving] = useState(false);
  const [availableCases, setAvailableCases] = useState([]);
  const [availableAgreements, setAvailableAgreements] = useState([]);

  const load = async () => {
    const [m, allCases, allAg] = await Promise.all([
      api.get(`/matters/${id}`).then((r) => r.data),
      api.get(`/cases`).then((r) => r.data).catch(() => []),
      api.get(`/agreements`).then((r) => r.data).catch(() => []),
    ]);
    setMatter(m);
    const linkedCases = allCases.filter((c) => m.linked_case_ids?.includes(c.id));
    const linkedAg = allAg.filter((a) => m.linked_agreement_ids?.includes(a.id));
    setCases(linkedCases);
    setAgreements(linkedAg);
    setAvailableCases(allCases.filter((c) => !m.linked_case_ids?.includes(c.id)));
    setAvailableAgreements(allAg.filter((a) => !m.linked_agreement_ids?.includes(a.id)));
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [id]);

  const save = async () => {
    setSaving(true);
    try {
      await api.patch(`/matters/${id}`, {
        title: matter.title,
        description: matter.description,
        category: matter.category,
        status: matter.status,
        jurisdiction: matter.jurisdiction,
        tags: matter.tags,
      });
      toast.success("Matter saved.");
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const remove = async () => {
    if (!window.confirm("Delete this matter? Linked cases and agreements are NOT deleted.")) return;
    await api.delete(`/matters/${id}`);
    toast.message("Matter deleted.");
    navigate("/app/matters");
  };

  const linkCase = async (caseId) => {
    await api.post(`/matters/${id}/link-case`, null, { params: { case_id: caseId } });
    load();
    toast.success("Case linked.");
  };
  const linkAgreement = async (aId) => {
    await api.post(`/matters/${id}/link-agreement`, null, { params: { agreement_id: aId } });
    load();
    toast.success("Agreement linked.");
  };

  if (!matter) return <div className="label-eyebrow">Loading…</div>;

  return (
    <div data-testid="matter-detail-page">
      <Link to="/app/matters" className="text-xs text-[#4A5568] inline-flex items-center gap-1 mb-4 hover:text-[#7B56C9]">
        <ArrowLeft size={12} /> All matters
      </Link>

      <PageHeader
        eyebrow={`Matter · ${matter.category.replace("-", " ")}`}
        title={matter.title}
        description={matter.description || "No description yet — add context, key parties, and the desired outcome."}
        action={
          <div className="flex items-center gap-2">
            <StatusBadge tone={statusTone(matter.status)}>{matter.status.replace("-", " ")}</StatusBadge>
            <button onClick={remove} className="p-2 border border-[#E8E3F0] hover:border-red-700 hover:text-red-700" data-testid="delete-matter-btn"><Trash2 size={14} /></button>
          </div>
        }
      />

      <div className="grid lg:grid-cols-3 gap-6">
        {/* Editable pane */}
        <div className="lg:col-span-2 space-y-6">
          <div className="border border-[#E8E3F0] bg-white p-6 space-y-4">
            <div>
              <label className="text-xs text-[#4A5568] mb-1 block label-eyebrow">Title</label>
              <Input value={matter.title} onChange={(e) => setMatter({ ...matter, title: e.target.value })} className="rounded-none border-[#E8E3F0]" data-testid="matter-title-input" />
            </div>
            <div>
              <label className="text-xs text-[#4A5568] mb-1 block label-eyebrow">Description</label>
              <textarea
                value={matter.description || ""}
                onChange={(e) => setMatter({ ...matter, description: e.target.value })}
                rows={5}
                className="w-full border border-[#E8E3F0] px-3 py-2 text-sm resize-y"
                placeholder="What is this matter about? Who is involved? What outcome are you seeking?"
                data-testid="matter-desc-input"
              />
            </div>
            <div className="grid sm:grid-cols-3 gap-4">
              <div>
                <label className="text-xs text-[#4A5568] mb-1 block label-eyebrow">Category</label>
                <select value={matter.category} onChange={(e) => setMatter({ ...matter, category: e.target.value })} className="w-full border border-[#E8E3F0] bg-white px-3 py-2 text-sm">
                  {CATEGORIES.map((c) => <option key={c} value={c}>{c.replace("-", " ")}</option>)}
                </select>
              </div>
              <div>
                <label className="text-xs text-[#4A5568] mb-1 block label-eyebrow">Status</label>
                <select value={matter.status} onChange={(e) => setMatter({ ...matter, status: e.target.value })} className="w-full border border-[#E8E3F0] bg-white px-3 py-2 text-sm" data-testid="matter-status-select">
                  {STATUSES.map((s) => <option key={s} value={s}>{s.replace("-", " ")}</option>)}
                </select>
              </div>
              <div>
                <label className="text-xs text-[#4A5568] mb-1 block label-eyebrow">Jurisdiction</label>
                <Input value={matter.jurisdiction || ""} onChange={(e) => setMatter({ ...matter, jurisdiction: e.target.value })} className="rounded-none border-[#E8E3F0]" placeholder="e.g. India, EU, US-CA" />
              </div>
            </div>
            <Button onClick={save} disabled={saving} className="rounded-none bg-[#2A0F5A] hover:bg-[#3D1F85] text-[#FAFAF8]" data-testid="save-matter-btn">
              <Save size={14} className="mr-1.5" /> {saving ? "Saving…" : "Save changes"}
            </Button>
          </div>

          {/* Linked cases */}
          <div className="border border-[#E8E3F0] bg-white">
            <div className="px-6 py-4 border-b border-[#E8E3F0] flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Scale size={16} className="text-[#7B56C9]" />
                <span className="font-serif text-xl">Cases</span>
                <span className="text-xs text-[#4A5568]">({cases.length})</span>
              </div>
              {availableCases.length > 0 && (
                <select
                  onChange={(e) => e.target.value && linkCase(e.target.value)}
                  defaultValue=""
                  className="text-xs border border-[#E8E3F0] px-2 py-1"
                  data-testid="link-case-select"
                >
                  <option value="">+ Link an existing case…</option>
                  {availableCases.map((c) => <option key={c.id} value={c.id}>{c.title}</option>)}
                </select>
              )}
            </div>
            {cases.length === 0 ? (
              <div className="px-6 py-4 text-sm text-[#4A5568]">
                No cases linked yet. When a matter escalates to litigation or filing, link the case here.{" "}
                <Link to="/app/cases/new" className="underline text-[#7B56C9]">Start a case →</Link>
              </div>
            ) : (
              <ul className="divide-y divide-[#E8E3F0]">
                {cases.map((c) => (
                  <li key={c.id} className="px-6 py-3 flex items-center justify-between">
                    <Link to={`/app/cases/${c.id}`} className="text-sm hover:text-[#7B56C9]">{c.title}</Link>
                    <ExternalLink size={12} className="text-[#4A5568]" />
                  </li>
                ))}
              </ul>
            )}
          </div>

          {/* Linked agreements */}
          <div className="border border-[#E8E3F0] bg-white">
            <div className="px-6 py-4 border-b border-[#E8E3F0] flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Handshake size={16} className="text-[#7B56C9]" />
                <span className="font-serif text-xl">Agreements</span>
                <span className="text-xs text-[#4A5568]">({agreements.length})</span>
              </div>
              {availableAgreements.length > 0 && (
                <select
                  onChange={(e) => e.target.value && linkAgreement(e.target.value)}
                  defaultValue=""
                  className="text-xs border border-[#E8E3F0] px-2 py-1"
                  data-testid="link-agreement-select"
                >
                  <option value="">+ Link an existing agreement…</option>
                  {availableAgreements.map((a) => <option key={a.id} value={a.id}>{a.title}</option>)}
                </select>
              )}
            </div>
            {agreements.length === 0 ? (
              <div className="px-6 py-4 text-sm text-[#4A5568]">
                No agreements linked yet.{" "}
                <Link to="/app/agreements" className="underline text-[#7B56C9]">Open Agreements →</Link>
              </div>
            ) : (
              <ul className="divide-y divide-[#E8E3F0]">
                {agreements.map((a) => (
                  <li key={a.id} className="px-6 py-3 text-sm">{a.title}</li>
                ))}
              </ul>
            )}
          </div>
        </div>

        {/* Sidebar activity */}
        <aside className="space-y-4">
          <div className="border border-[#E8E3F0] bg-white p-5">
            <div className="label-eyebrow mb-2">Timeline</div>
            <div className="text-xs text-[#4A5568]">Opened {new Date(matter.created_at).toLocaleDateString()}</div>
            <div className="text-xs text-[#4A5568]">Updated {new Date(matter.updated_at).toLocaleDateString()}</div>
          </div>
          <div className="border border-[#E8E3F0] bg-white p-5">
            <div className="label-eyebrow mb-2">Next steps</div>
            <ul className="text-sm space-y-2 text-[#2A0F5A]/85">
              <li className="flex items-start gap-2"><Plus size={12} className="mt-1 text-[#7B56C9]" /> Draft or import the agreement</li>
              <li className="flex items-start gap-2"><Plus size={12} className="mt-1 text-[#7B56C9]" /> Send it for signature</li>
              <li className="flex items-start gap-2"><Plus size={12} className="mt-1 text-[#7B56C9]" /> Track its obligations</li>
              <li className="flex items-start gap-2"><Plus size={12} className="mt-1 text-[#7B56C9]" /> Escalate to a case if needed</li>
            </ul>
          </div>
        </aside>
      </div>
    </div>
  );
}
